/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api.ts
 🗓️ Created: 2025-12-23
 🧩 Description:
    API client for Custom Tasks module.
 ────────────────────────────────────────────────────────────────*/

import { api } from '../../lib/api';

// Types
export interface Script {
    name: string;
    path: string;
    size: number;
    modified: string;
}

export interface CronProfile {
    id: number;
    name: string;
    schedule: string;
}

export interface CustomTask {
    id: number;
    uuid: string;
    name: string;
    description: string | null;
    scriptPath: string;
    scriptArgs: string | null;
    cronProfileId: number | null;
    timeoutSeconds: number;
    runAsUser: string;
    enabled: boolean;
    lastRun: string | null;
    lastStatus: string | null;
    lastExitCode: number | null;
    lastError: string | null;
    runCount: number;
    tenantId: number;
    createdAt: string;
    updatedAt: string;
    cronProfile?: CronProfile | null;
}

export interface CustomTaskExecution {
    id: number;
    uuid: string;
    customTaskId: number;
    startedAt: string;
    finishedAt: string | null;
    durationMs: number | null;
    status: string;
    exitCode: number | null;
    output: string | null;
    errorMessage: string | null;
    triggeredBy: string | null;
    triggeredByUserId: number | null;
}

export interface CreateCustomTaskInput {
    name: string;
    description?: string;
    scriptPath: string;
    scriptArgs?: string;
    cronProfileId?: number | null;
    timeoutSeconds?: number;
    runAsUser?: string;
    enabled?: boolean;
}

export interface UpdateCustomTaskInput extends Partial<CreateCustomTaskInput> { }

interface ApiResponse<T> {
    success: boolean;
    data: T;
    pagination?: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
    message?: string;
    error?: string;
}

/**
 * Fetch available scripts
 */
export const fetchScripts = async (): Promise<Script[]> => {
    const response = await api.get<ApiResponse<Script[]>>('/api/maintenance/custom-tasks/scripts');
    return response.data.data;
};

/**
 * Fetch cron profiles for dropdown
 */
export const fetchCronProfiles = async (): Promise<CronProfile[]> => {
    const response = await api.get<ApiResponse<CronProfile[]>>('/api/maintenance/custom-tasks/cron-profiles');
    return response.data.data;
};

/**
 * Fetch custom tasks with pagination
 */
export const fetchCustomTasks = async (params: {
    page?: number;
    limit?: number;
    search?: string;
    enabled?: boolean;
}): Promise<ApiResponse<CustomTask[]>> => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.set('page', String(params.page));
    if (params.limit) queryParams.set('limit', String(params.limit));
    if (params.search) queryParams.set('search', params.search);
    if (params.enabled !== undefined) queryParams.set('enabled', String(params.enabled));

    const response = await api.get<ApiResponse<CustomTask[]>>(
        `/api/maintenance/custom-tasks?${queryParams.toString()}`
    );
    return response.data;
};

/**
 * Fetch single custom task
 */
export const fetchCustomTask = async (id: number | string): Promise<CustomTask> => {
    const response = await api.get<ApiResponse<CustomTask>>(`/api/maintenance/custom-tasks/${id}`);
    return response.data.data;
};

/**
 * Create custom task
 */
export const createCustomTask = async (data: CreateCustomTaskInput): Promise<CustomTask> => {
    const response = await api.post<ApiResponse<CustomTask>>('/api/maintenance/custom-tasks', data);
    return response.data.data;
};

/**
 * Update custom task
 */
export const updateCustomTask = async (id: number | string, data: UpdateCustomTaskInput): Promise<CustomTask> => {
    const response = await api.put<ApiResponse<CustomTask>>(`/api/maintenance/custom-tasks/${id}`, data);
    return response.data.data;
};

/**
 * Delete custom task
 */
export const deleteCustomTask = async (id: number | string): Promise<void> => {
    await api.delete(`/api/maintenance/custom-tasks/${id}`);
};

/**
 * Execute custom task manually
 */
export const executeCustomTask = async (id: number | string): Promise<CustomTaskExecution> => {
    const response = await api.post<ApiResponse<CustomTaskExecution>>(
        `/api/maintenance/custom-tasks/${id}/execute`
    );
    return response.data.data;
};

/**
 * Fetch execution history for a task
 */
export const fetchTaskExecutions = async (taskId: number | string, limit = 50): Promise<CustomTaskExecution[]> => {
    const response = await api.get<ApiResponse<CustomTaskExecution[]>>(
        `/api/maintenance/custom-tasks/${taskId}/executions?limit=${limit}`
    );
    return response.data.data;
};

/**
 * Fetch single execution
 */
export const fetchExecution = async (executionId: number): Promise<CustomTaskExecution> => {
    const response = await api.get<ApiResponse<CustomTaskExecution>>(
        `/api/maintenance/custom-tasks/executions/${executionId}`
    );
    return response.data.data;
};
